import { useState } from "react";
import { useLang } from "@/contexts/LangContext";
import { api } from "@/lib/api";
import { X, KeyRound, Eye, EyeOff, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

export default function ChangePasswordModal({ onClose }) {
  const { t } = useLang();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  async function submit(e) {
    e.preventDefault();
    if (next.length < 6) {
      toast.error(t("pwd.tooShort"));
      return;
    }
    if (next !== confirm) {
      toast.error(t("pwd.mismatch"));
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/auth/change-password", { current_password: current, new_password: next });
      toast.success(t("pwd.success"));
      onClose?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-[80] bg-black/80 backdrop-blur-sm grid place-items-center p-4"
      onClick={onClose}
      data-testid="change-password-modal"
    >
      <form
        onSubmit={submit}
        onClick={(e) => e.stopPropagation()}
        className="card-luxury p-6 w-full max-w-md"
      >
        <div className="flex justify-between items-start mb-5">
          <div>
            <div className="text-xs uppercase tracking-[0.3em] text-gold flex items-center gap-2">
              <KeyRound className="h-4 w-4" /> Keamanan
            </div>
            <h2 className="text-xl font-heading mt-1">{t("pwd.change")}</h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="pwd-close-btn"
            className="p-1"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="space-y-3">
          <div>
            <label className="block text-[10px] uppercase tracking-[0.2em] text-foreground/50 mb-1">
              {t("pwd.current")}
            </label>
            <input
              type={show ? "text" : "password"}
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              required
              autoComplete="current-password"
              data-testid="pwd-current-input"
              className="w-full bg-charcoal-900 border border-charcoal-600 rounded-md px-3 py-2"
            />
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-[0.2em] text-foreground/50 mb-1">
              {t("pwd.new")}
            </label>
            <input
              type={show ? "text" : "password"}
              value={next}
              onChange={(e) => setNext(e.target.value)}
              required
              minLength={6}
              autoComplete="new-password"
              data-testid="pwd-new-input"
              className="w-full bg-charcoal-900 border border-charcoal-600 rounded-md px-3 py-2"
            />
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-[0.2em] text-foreground/50 mb-1">
              {t("pwd.confirm")}
            </label>
            <input
              type={show ? "text" : "password"}
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              required
              minLength={6}
              autoComplete="new-password"
              data-testid="pwd-confirm-input"
              className="w-full bg-charcoal-900 border border-charcoal-600 rounded-md px-3 py-2"
            />
          </div>

          <button
            type="button"
            onClick={() => setShow((s) => !s)}
            className="text-xs text-foreground/60 hover:text-gold inline-flex items-center gap-1.5"
            data-testid="pwd-toggle-show"
          >
            {show ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
            {show ? "Sembunyikan" : "Tampilkan"}
          </button>
        </div>

        <button
          type="submit"
          disabled={submitting}
          data-testid="pwd-submit-btn"
          className="btn-gold w-full justify-center mt-5 disabled:opacity-50"
        >
          <ShieldCheck className="h-4 w-4" />
          {submitting ? "..." : t("pwd.submit")}
        </button>
      </form>
    </div>
  );
}
